import { FC, ReactNode } from 'react';
import { Select, SelectProps, Button, Space, theme } from 'antd';
import { EntityRecordSelectOption, useEntityRecordUI } from './useEntityRecordUI';
import Icon from '../Icon';
import FieldLabel from '../FieldLabel';
import FlexCol from '../FlexCol';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { useKeybindings, ariaKeyshortcutsFor } from '@/keyboard';

/**
 * Props for the EntityRecordUI component.
 */
interface EntityRecordUIProps {
  /**
   * Entity ID to fetch records from (required)
   */
  entityId: string;
  /**
   * Entity group ID for filtering and reference storage (optional)
   */
  entityGroupId?: string;
  /**
   * Disables all user interactions with the selector.
   * When true, the select dropdown cannot be opened and no changes can be made.
   */
  disabled?: boolean;
  /**
   * Hides the control buttons (View Record) in the dropdown footer.
   * Useful for simplified UI presentations where inline actions aren't needed.
   */
  hideControls?: boolean;
  /**
   * Custom CSS styles applied to the Select component itself.
   * Use to override width, margins, or other select-specific styles.
   */
  style?: React.CSSProperties;
  /**
   * Custom CSS styles applied to the dropdown menu popup.
   * Use to control the width and appearance of the options list.
   */
  dropdownStyle?: React.CSSProperties;
  /**
   * Size of the Select component.
   * Defaults to 'medium'.
   */
  size?: SelectProps['size'];
  /**
   * Select component variant (outlined, borderless, or filled).
   * Defaults to 'filled'.
   */
  variant?: SelectProps['variant'];

  /**
   * Controls the save behavior mode of the component.
   *
   * **Internal Save Mode (default):**
   * - When false or undefined (default behavior)
   * - Component saves changes automatically via context providers
   * - Use for simple forms where context propagation is fast
   * - externalOnChange is ignored in this mode
   *
   * **External Save Mode:**
   * - When true
   * - Component only updates local UI state
   * - Parent component must handle all persistence via externalOnChange
   * - Use for complex structures (tables, grids) where context updates are slow
   * - externalOnChange becomes REQUIRED
   *
   * @example
   * // Internal save mode (default) - for simple forms
   * <EntityRecordUI />
   *
   * @example
   * // External save mode - for tables/complex structures
   * <EntityRecordUI
   *   enableExternalOnChange={true}
   *   externalOnChange={(recordId, record) => saveToFirestore(recordId)}
   * />
   */
  enableExternalOnChange?: boolean;

  /**
   * External change handler for record selection changes.
   *
   * **REQUIRED when enableExternalOnChange is true.**
   *
   * This callback receives the selected record ID and full record data,
   * allowing the parent component to handle persistence directly.
   *
   * @param value - The selected record's docId, or undefined if cleared
   * @param entityRecord - The complete EntityRecord object with all fields, or undefined if cleared
   *
   * @example
   * // In CellUI for table cells
   * externalOnChange={(recordId, record) => {
   *   if (recordId) {
   *     saveEntityRecordReference(recordId, record);
   *   } else {
   *     clearEntityRecordReference();
   *   }
   * }}
   */
  externalOnChange?: (value: string | undefined, entityRecord?: EntityRecordSelectOption) => void;

  /**
   * External value for the selected record ID.
   *
   * **Used with enableExternalOnChange for controlled components.**
   *
   * When provided, this value is used instead of reading from entityReferences
   * in Firestore. This is essential for table cells to avoid N+1 query problems.
   *
   * @example
   * // In table cells
   * <EntityRecordUI
   *   value={cellValue}
   *   enableExternalOnChange={true}
   *   externalOnChange={(recordId) => saveCellValue(recordId)}
   * />
   */
  value?: string;

  /**
   * Optional label to display above the selector.
   * When provided, renders a FieldLabel above the selector.
   */
  label?: ReactNode;

  /**
   * Whether the field is required. Only used when label is provided.
   */
  required?: boolean;

  /**
   * When true, changes to this entity reference trigger immediate search index sync.
   */
  triggerSearchSync?: boolean;
}

/**
 * @deprecated Use ReferenceSelect.ts instead.
 *
 * EntityRecordUI is deprecated.
 * 
 * Please migrate to ReferenceSelect for all new usages.
 * 
 * -- Old Docs --
 * EntityRecordUI - A self-contained searchable selector for entity records with modal viewing.
 * 
 * **Purpose:**
 * Provides a searchable dropdown interface for selecting entity records, with
 * integrated search, filtering, modal viewing, and automatic persistence.
 * Follows the useFieldUI pattern for self-contained state management.
 *
 * **Self-Contained Design:**
 * - Manages its own state and persistence internally
 * - No context providers required for basic usage
 * - Reads from and saves to entityReferences in parent document
 * - Debounced saves (600ms) with dirty tracking
 * - Loads records in background immediately when entityId is available
 * - Displays cached record name from entityReferences (instant display)
 * - Only shows loading when recordId exists but no cached name yet
 *
 * **Save Modes:**
 * 1. Internal Save (default): Automatically persists to entityReferences
 * 2. External Save: Delegates persistence to parent via externalOnChange prop
 *
 * @example
 * // Basic usage with internal saves (forms) - most common
 * <EntityRecordUI entityId="contacts" entityGroupId="primaryContact" />
 *
 * @example
 * // With external saves (for tables/complex structures)
 * <EntityRecordUI
 *   entityId="products"
 *   entityGroupId={rowId}
 *   enableExternalOnChange={true}
 *   externalOnChange={(recordId) => saveCellValue(recordId)}
 * />
 *
 * @example
 * // With custom styling and controls hidden
 * <EntityRecordUI
 *   entityId="users"
 *   style={{ width: 400 }}
 *   dropdownStyle={{ width: 500 }}
 *   hideControls={true}
 *   disabled={readOnly}
 * />
 */
const EntityRecordUI: FC<EntityRecordUIProps> = ({
  entityId,
  entityGroupId,
  disabled,
  hideControls,
  style,
  dropdownStyle,
  enableExternalOnChange,
  externalOnChange,
  value,
  variant = 'filled',
  size,
  label,
  required,
  triggerSearchSync,
}) => {
  const { isPdfExportMode } = usePdfExportContext();

  const {
    isSelectOpen,
    searchQuery,
    recordId,
    recordName,
    isReadOnly,
    loading,
    error,
    options,
    optionsMetadata,
    setSearchQuery,
    handleChange,
    handleClear,
    handleOpenButtonClick,
    handleOpenChange,
  } = useEntityRecordUI({
    entityId,
    entityGroupId,
    enableExternalOnChange,
    externalOnChange,
    value,
    triggerSearchSync,
    disabled,
  });

  const { token } = theme.useToken();

  const { onKeyDown: entityKeyDown } = useKeybindings({
    'field.openDetail': handleOpenButtonClick,
    ...(isSelectOpen ? {} : { 'field.openPopup': () => handleOpenChange(true) }),
  });

  const selectUI = (
    <Space.Compact
      style={{
        display: 'flex',
        width: 300,
        ...style
      }}
    >
      <Select
        key={`${entityId}-${entityGroupId}`}
        id={`entity-record-ui-${entityId}-${entityGroupId}-${recordId}`}
        aria-label={`Select ${label ?? 'record'}${recordName ? `: ${recordName}` : ''}`}
        aria-keyshortcuts={ariaKeyshortcutsFor('field.openDetail')}
        onKeyDown={entityKeyDown}
        open={isSelectOpen}
        onOpenChange={handleOpenChange}
        size={size}
        style={{
          width: hideControls ? 300 : 280,
          ...style
        }}
        styles={{
          popup: {
            root: {
              width: 400,
              ...dropdownStyle
            }
          }
        }}
        variant={variant}
        showSearch={{
          filterOption: false,
          onSearch: setSearchQuery,
          searchValue: searchQuery,
        }}
        allowClear
        placeholder='Search Records'
        onChange={handleChange}
        onClear={handleClear}
        value={recordId}
        disabled={hideControls ? false : disabled || isReadOnly}
        virtual={true}
        listHeight={300}
        notFoundContent={
          loading ? 'Loading...' :
            error ? `Error: ${error}` :
              !entityId ? 'No entity selected' :
                'No results found'
        }
        popupRender={isPdfExportMode ? undefined : menu => (
          <div>
            {menu}
            {!hideControls && (
              <Button
                type="text"
                style={{ width: '100%' }}
                onClick={handleOpenButtonClick}
                disabled={disabled || isReadOnly}
                aria-label="Open Record"
              >
                View Record
                <Icon.MoreVertical />
              </Button>
            )}
          </div>
        )}
        options={options}
        labelInValue={false}
        labelRender={() => recordName || 'Unnamed Record'}
        optionRender={(option) => {
          const metadata = optionsMetadata.get(option.data.value as string);
          return metadata?.name || 'Unnamed Record';
        }}
      />
      {!hideControls && !isPdfExportMode && (
        <Button
          type="text"
          tabIndex={-1}
          style={{
            width: 20,
            color: token.colorTextQuaternary,
            backgroundColor: token.colorBgBase
          }}
          onClick={handleOpenButtonClick}
          disabled={disabled || isReadOnly}
          icon={<Icon.MoreVertical />}
          aria-label="Open Record"
        />
      )}
    </Space.Compact>
  );

  if (label) {
    return (
      <FlexCol gap={0} noGrow>
        <FieldLabel
          label={label}
          required={required}
        />
        {selectUI}
      </FlexCol>
    );
  }

  return selectUI;
};

export default EntityRecordUI;